import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Box,
  Typography,
  IconButton,
  Divider,
  Fade,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from './styled/StyledComponents';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { styled, keyframes } from '@mui/material/styles';
import { RESONANCE_URL } from '../config/links';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const FooterContainer = styled(Box)(({ theme }) => ({
  background: colors.primary,
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(12),
  },
}));

const FooterBackground = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '100%',
  background: `linear-gradient(135deg, ${colors.primary} 0%, #0A7054 100%)`,
  opacity: 0.95,
  zIndex: 0,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    background:
      'radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.04) 0%, transparent 50%)',
    zIndex: 1,
  },
});

const FooterHeading = styled(Typography)({
  color: 'white',
  fontWeight: 700,
  fontSize: '0.8rem',
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  marginBottom: 20,
  opacity: 0.85,
});

const FooterLink = styled('a')({
  display: 'block',
  color: 'rgba(255, 255, 255, 0.75)',
  fontSize: '0.95rem',
  textDecoration: 'none',
  padding: '5px 0',
  transition: 'color 0.2s ease, transform 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    color: '#FFFFFF',
    transform: 'translateX(3px)',
  },
});

const SocialButton = styled(IconButton)({
  color: 'rgba(255, 255, 255, 0.8)',
  transition: 'all 0.3s ease',
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  '&:hover': {
    color: 'white',
    transform: 'translateY(-4px)',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
});

const ContactItem = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: 14,
  color: 'rgba(255, 255, 255, 0.8)',
  gap: 12,
  '& .icon': {
    marginTop: 2,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
});

const CATEGORY_KEYS = ['solutions', 'company', 'resources', 'legal'];

const Footer = ({ scrollToSection }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    const el = document.getElementById('footer');
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const contactInfo = [
    { icon: <LocationOnIcon fontSize="small" />, text: t('footer.contactInfo.address') },
    { icon: <EmailIcon fontSize="small" />, text: t('footer.contactInfo.email') },
    { icon: <PhoneIcon fontSize="small" />, text: t('footer.contactInfo.phone') },
  ];

  const socialIcons = [
    { icon: <LinkedInIcon />, label: 'LinkedIn', url: 'https://linkedin.com' },
    { icon: <TwitterIcon />, label: 'Twitter', url: 'https://twitter.com' },
  ];

  const handleLinkClick = (href, e) => {
    if (href === 'resonance-external') {
      e.preventDefault();
      window.open(RESONANCE_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    if (href && href.startsWith('mailto:')) return; // native behavior
    if (href === '#' || !href) return; // placeholder
    // Otherwise treat as section key and smooth-scroll
    e.preventDefault();
    if (scrollToSection) scrollToSection(href);
  };

  const resolveHref = (href) => {
    if (!href) return '#';
    if (href === 'resonance-external') return RESONANCE_URL;
    if (href.startsWith('mailto:')) return href;
    if (href === '#') return '#';
    return `#${href}-section`;
  };

  return (
    <FooterContainer id="footer">
      <FooterBackground />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={4}>
            <Fade in={isVisible} timeout={800}>
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    color: 'white',
                    fontWeight: 800,
                    mb: 2,
                    letterSpacing: '-0.01em',
                  }}
                >
                  {t('footer.companyName')}
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.85)',
                    mb: 3,
                    lineHeight: 1.7,
                    maxWidth: 420,
                    fontSize: '0.95rem',
                  }}
                >
                  {t('footer.description')}
                </Typography>

                <Box sx={{ mb: 3 }}>
                  {contactInfo.map((item, index) => (
                    <ContactItem key={index}>
                      <Box className="icon">{item.icon}</Box>
                      <Typography variant="body2">{item.text}</Typography>
                    </ContactItem>
                  ))}
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {socialIcons.map((social, index) => (
                    <SocialButton
                      key={index}
                      component="a"
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      sx={{
                        animation: isVisible
                          ? `${fadeInUp} 0.5s ease forwards ${0.2 + index * 0.1}s`
                          : 'none',
                        opacity: 0,
                      }}
                    >
                      {social.icon}
                    </SocialButton>
                  ))}
                </Box>
              </Box>
            </Fade>
          </Grid>

          {CATEGORY_KEYS.map((key) => {
            const category = t(`footer.categories.${key}`, { returnObjects: true });
            if (!category || !Array.isArray(category.links)) return null;
            return (
              <Grid item xs={6} md={2} key={key} sx={{ mt: { md: 1 } }}>
                <FooterHeading>{category.title}</FooterHeading>
                {category.links.map((link, idx) => (
                  <FooterLink
                    key={idx}
                    href={resolveHref(link.href)}
                    target={link.href === 'resonance-external' ? '_blank' : undefined}
                    rel={link.href === 'resonance-external' ? 'noopener noreferrer' : undefined}
                    onClick={(e) => handleLinkClick(link.href, e)}
                  >
                    {link.label}
                  </FooterLink>
                ))}
              </Grid>
            );
          })}
        </Grid>

        <Divider
          sx={{
            my: { xs: 4, md: 6 },
            borderColor: 'rgba(255, 255, 255, 0.12)',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background:
                'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
              backgroundSize: '200% 100%',
              animation: `${shimmer} 4s linear infinite`,
            },
          }}
        />

        <Fade in={isVisible} timeout={800} style={{ transitionDelay: '400ms' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255, 255, 255, 0.8)', textAlign: 'center' }}
            >
              {t('footer.copyright')}
            </Typography>
          </Box>
        </Fade>
      </Container>
    </FooterContainer>
  );
};

export default Footer;
